import React, { useEffect, useRef, useState } from "react";
import "./AcceptingForm.css";
import Footer from "./Footer";

const GOOGLE_MAPS_SCRIPT_ID = "google-maps-places-script";

const INITIAL_FORM = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  address1: "",
  city: "",
  state: "",
  postalCode: "",
  country: "US",
  referral: "",
  servicesOfInterest: [],
};

const SERVICE_OPTIONS = [
  "Vessel Services",
  "Estate Services",
  "Wardrobe Services",
];

let googleMapsPlacesPromise;

function loadGoogleMapsPlaces(apiKey) {
  if (!apiKey) return Promise.resolve(null);

  if (window.google?.maps?.places) {
    return Promise.resolve(window.google);
  }

  if (googleMapsPlacesPromise) {
    return googleMapsPlacesPromise;
  }

  googleMapsPlacesPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(window.google));
      existingScript.addEventListener("error", reject);
      return;
    }

    const script = document.createElement("script");
    const params = new URLSearchParams({
      key: apiKey,
      libraries: "places",
    });

    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = reject;

    document.head.appendChild(script);
  });

  return googleMapsPlacesPromise;
}

function getAddressComponent(components, type, nameFormat = "long_name") {
  return (
    components.find((component) => component.types.includes(type))?.[
      nameFormat
    ] || ""
  );
}

function getCity(components) {
  return (
    getAddressComponent(components, "locality") ||
    getAddressComponent(components, "postal_town") ||
    getAddressComponent(components, "sublocality") ||
    getAddressComponent(components, "administrative_area_level_2")
  );
}

function parsePlaceAddress(place) {
  const components = place.address_components || [];
  const streetNumber = getAddressComponent(components, "street_number");
  const route = getAddressComponent(components, "route");
  const postalCode = getAddressComponent(components, "postal_code");
  const postalCodeSuffix = getAddressComponent(
    components,
    "postal_code_suffix",
  );

  return {
    address1: [streetNumber, route].filter(Boolean).join(" "),
    city: getCity(components),
    state: getAddressComponent(
      components,
      "administrative_area_level_1",
      "short_name",
    ),
    postalCode: [postalCode, postalCodeSuffix].filter(Boolean).join("-"),
    country: getAddressComponent(components, "country", "short_name"),
  };
}

export default function AcceptingForm({ isOpen, onClose }) {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [submitState, setSubmitState] = useState("idle");
  const [submitMessage, setSubmitMessage] = useState("");
  const addressInputRef = useRef(null);

  useEffect(() => {
    let autocomplete;
    let listener;
    let isMounted = true;
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    loadGoogleMapsPlaces(apiKey)
      .then((google) => {
        if (!google || !isMounted || !addressInputRef.current) return;

        autocomplete = new google.maps.places.Autocomplete(
          addressInputRef.current,
          {
            componentRestrictions: { country: "us" },
            fields: ["address_components", "formatted_address"],
            types: ["address"],
          },
        );

        listener = autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();
          const parsedAddress = parsePlaceAddress(place);

          setFormData((current) => ({
            ...current,
            address1:
              parsedAddress.address1 ||
              place.formatted_address ||
              current.address1,
            city: parsedAddress.city || current.city,
            state: parsedAddress.state || current.state,
            postalCode: parsedAddress.postalCode || current.postalCode,
            country: parsedAddress.country || current.country,
          }));
        });
      })
      .catch(() => {
        // Address autocomplete is progressive enhancement; manual entry still works.
      });

    return () => {
      isMounted = false;

      if (listener?.remove) {
        listener.remove();
      }
    };
  }, []);

  const updateField = (field, value) => {
    setFormData((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const updateService = (service, checked) => {
    setFormData((current) => {
      const servicesOfInterest = checked
        ? [...current.servicesOfInterest, service]
        : current.servicesOfInterest.filter((item) => item !== service);

      return {
        ...current,
        servicesOfInterest,
      };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitState("submitting");
    setSubmitMessage("");

    try {
      const response = await fetch("/api/reservations", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });
      const result = await response.json();

      if (!response.ok || !result.ok) {
        throw new Error(result.error || "Unable to submit reservation");
      }

      setFormData(INITIAL_FORM);
      setSubmitState("success");
      setSubmitMessage(
        "Thank you. Your reservation request has been received.",
      );
    } catch (error) {
      setSubmitState("error");
      setSubmitMessage(
        "We could not submit your reservation. Please try again shortly.",
      );
    }
  };

  return (
    <div className={`accepting-form-overlay ${isOpen ? "open" : ""}`}>
      <div className="accepting-form-content">
        <div className="accepting-close-btn" onClick={onClose}>
          &#10005; Close
        </div>

        <div className="accepting-req-membership">REQUEST MEMBERSHIP</div>

        <h2 className="accepting-title">
          Now <span className="accent">Accepting</span>
          <br />
          Reservations
        </h2>

        <p className="accepting-desc">
          Sudsies BLACK LABEL® membership remains complimentary and is extended
          strictly by invitation. To reserve a place on the waiting list for
          membership consideration, please share a few details with us by
          completing the form below. When an opportunity becomes available, a
          dedicated member representative will contact you directly.
        </p>

        <div className="accepting-divider"></div>

        <div className="accepting-section-title">CONTACT INFORMATION</div>

        <form onSubmit={handleSubmit}>
          <div className="accepting-form-group">
            <input
              type="text"
              placeholder="First Name*"
              value={formData.firstName}
              onChange={(event) => updateField("firstName", event.target.value)}
              required
            />
          </div>
          <div className="accepting-form-group">
            <input
              type="text"
              placeholder="Last Name*"
              value={formData.lastName}
              onChange={(event) => updateField("lastName", event.target.value)}
              required
            />
          </div>
          <div className="accepting-form-group">
            <input
              type="tel"
              placeholder="Mobile Phone*"
              value={formData.phone}
              onChange={(event) => updateField("phone", event.target.value)}
              required
            />
          </div>
          <div className="accepting-form-group">
            <input
              type="email"
              placeholder="Email Address*"
              value={formData.email}
              onChange={(event) => updateField("email", event.target.value)}
              required
            />
          </div>
          <div className="accepting-form-group">
            <input
              ref={addressInputRef}
              type="text"
              placeholder="Service Address* (Primary address)"
              value={formData.address1}
              onChange={(event) => updateField("address1", event.target.value)}
              autoComplete="street-address"
              required
            />
          </div>
          <div className="accepting-form-group">
            <input
              type="text"
              placeholder="City"
              value={formData.city}
              onChange={(event) => updateField("city", event.target.value)}
            />
          </div>
          <div className="accepting-form-group accepting-form-row">
            <input
              type="text"
              placeholder="State"
              value={formData.state}
              onChange={(event) => updateField("state", event.target.value)}
            />
            <input
              type="text"
              placeholder="Postal Code"
              value={formData.postalCode}
              onChange={(event) =>
                updateField("postalCode", event.target.value)
              }
            />
          </div>
          <div className="accepting-form-group">
            <input
              type="text"
              placeholder="Country"
              value={formData.country}
              onChange={(event) => updateField("country", event.target.value)}
            />
          </div>
          <div className="accepting-form-group">
            <label
              className="accepting-section-title"
              htmlFor="referral"
              style={{ marginTop: "40px" }}
            >
              Referral Source
            </label>
            <input
              id="referral"
              className="accepting-referral-input"
              type="text"
              placeholder="Who may we thank for referring you for BLACK LABEL® membership consideration?"
              value={formData.referral}
              onChange={(event) => updateField("referral", event.target.value)}
            />
          </div>

          <div className="accepting-section-title" style={{ marginTop: "40px" }}>
            SERVICES OF INTEREST TO YOUR HOUSEHOLD
          </div>

          <div className="accepting-checkbox-group">
            {SERVICE_OPTIONS.map((service) => (
              <label className="accepting-checkbox-item" key={service}>
                <input
                  type="checkbox"
                  checked={formData.servicesOfInterest.includes(service)}
                  onChange={(event) =>
                    updateService(service, event.target.checked)
                  }
                />{" "}
                {service}
              </label>
            ))}
          </div>

          {submitMessage ? (
            <div className={`accepting-submit-message ${submitState}`}>
              {submitMessage}
            </div>
          ) : null}

          <button
            type="submit"
            className="accepting-submit-btn"
            disabled={submitState === "submitting"}
          >
            {submitState === "submitting"
              ? "SUBMITTING..."
              : "SUBMIT MY RESERVATION"}
          </button>
        </form>

        <Footer />
      </div>

      <div
        className={`accepting-success-panel ${
          submitState === "success" ? "show" : ""
        }`}
        aria-hidden={submitState !== "success"}
      >
        <div className="accepting-close-btn" onClick={onClose}>
          &#10005; Close
        </div>

        <div className="accepting-success-content">
          <div className="accepting-req-membership">REQUEST RECEIVED</div>

          <h2 className="accepting-title">
            Thank <span className="accent">You</span>
          </h2>

          <div className="accepting-divider"></div>

          <p className="accepting-desc accepting-success-desc">
            Thank you for submitting your form. A dedicated member
            representative will be in contact with you shortly.
          </p>
        </div>
      </div>
    </div>
  );
}
